import {
  Button,
  ButtonIcon,
  Grow,
  List,
  ListItem,
  ListItemText,
  Surface,
  VirtualizedList,
  useClickAwayListener,
  useDebouncedState,
  useMagnet,
  useScrollThreshold,
} from '@mint/ui';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AdminPagePickerContext } from './admin-page-picker-context';
import { AdminPagePickerProps } from './admin-page-picker-props';
import { AdminTableChip } from '../admin-table-chip';
import { WedgeDownIcon } from '@mint/icons';
import styled from 'styled-components';
import { useListPagesQuery } from '../../../queries';

const Div = styled.div`
  position: relative;
`;

const PAGE_SIZE = 40;

export const AdminPagePicker: React.FunctionComponent<
  AdminPagePickerProps
> = props => {
  const {
    color,
    children,
    label = 'Select Page',
    onClick,
    onPageSelect,
  } = props;
  const [actionsVisible, setActionsVisible] = useState(false);
  const [, style] = useMagnet({ anchor: 'north-east', position: 'inside' });

  const ref = useClickAwayListener((): void => setActionsVisible(false), {
    enabled: actionsVisible,
  });

  const openDropdown = useCallback(() => {
    setActionsVisible(true);
  }, []);

  const closeDropdown = useCallback(() => {
    setActionsVisible(false);
  }, []);

  const context = useMemo(
    () => ({
      close: closeDropdown,
      open: openDropdown,
    }),
    [openDropdown, closeDropdown],
  );

  const [query, setQuery] = useDebouncedState('', 400);

  const { data, error, loading, fetchMore } = useListPagesQuery(
    { first: PAGE_SIZE, query: query.trim() },
    { fetchPolicy: 'cache-and-network' },
  );

  const scrollableRef = useRef<HTMLDivElement>(null);

  const bindScrollThreshold = useScrollThreshold(
    () => {
      if (!data || !data.pages.pageInfo.hasNextPage) {
        return;
      }
      fetchMore({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            ...prev,
            pages: {
              ...prev.pages,
              edges: [...prev.pages.edges, ...fetchMoreResult.pages.edges],
              pageInfo: fetchMoreResult.pages.pageInfo,
            },
          };
        },
        variables: {
          first: PAGE_SIZE,
          after: data ? data.pages.pageInfo.endCursor : undefined,
        },
      });
    },
    {
      disabled: loading,
      ref: scrollableRef,
    },
  );

  const onQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
    },
    [],
  );

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({ top: 0 });
    }
  }, [query]);

  const onListItemClick = useCallback(
    (page: { id: string; title: string }) => {
      closeDropdown();
      if (onPageSelect) {
        onPageSelect(page);
      }
    },
    [onPageSelect, closeDropdown],
  );

  return (
    <Div ref={ref}>
      <Button
        onClick={openDropdown}
        variant="raised"
        color={color}
        iconRight={
          <ButtonIcon>
            <WedgeDownIcon />
          </ButtonIcon>
        }
      >
        {label}
      </Button>
      <Grow
        in={actionsVisible}
        style={{
          ...style,
          height: 300,
          right: 0,
          top: 0,
          transform: 'rotate3d(0)',
          transformOrigin: 'top right',
          position: 'absolute',
          zIndex: 1000,
          whiteSpace: 'nowrap',
          width: 200,
        }}
      >
        <Surface
          z={8}
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <input onChange={onQueryChange} />
          <AdminPagePickerContext.Provider value={context}>
            <VirtualizedList
              {...bindScrollThreshold}
              batchSize={2}
              component={List}
              initialBatchSize={40}
              overscan={200}
              ref={scrollableRef}
              style={{ flex: 1, margin: 0 }}
              threshold={200}
            >
              {data && data.pages
                ? data.pages.edges.map(
                    ({ node: page }): React.ReactElement => (
                      <ListItem
                        button
                        key={page.id}
                        onClick={() => onListItemClick(page)}
                      >
                        <ListItemText>
                          {page.title ? page.title : <i>Untitled</i>}
                          {!page.publishAt && (
                            <AdminTableChip>Draft</AdminTableChip>
                          )}
                        </ListItemText>
                      </ListItem>
                    ),
                  )
                : null}
            </VirtualizedList>
          </AdminPagePickerContext.Provider>
        </Surface>
      </Grow>
    </Div>
  );
};
